import React from "react";
import { Link } from "react-router-dom";

const COMPANY_NAME = "SSQUARE LOGIC";
const PRODUCT_NAME = "StayServe";
const EFFECTIVE_DATE = "18 August 2026";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-stone-200 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
          <Link
            to="/"
            className="flex items-center gap-3 transition-opacity hover:opacity-80"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500 text-xl text-white shadow-lg shadow-orange-500/20">
              🔥
            </div>

            <div>
              <div className="text-lg font-black tracking-tight">
                {PRODUCT_NAME}{" "}
                <span className="text-orange-500"></span>
              </div>

              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-stone-400">
                Hospitality Management Platform
              </div>
            </div>
          </Link>

          <Link
            to="/"
            className="rounded-xl border border-stone-300 bg-white px-5 py-2.5 text-sm font-bold text-stone-800 transition-all hover:border-orange-400 hover:text-orange-500"
          >
            ← Back to Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-stone-800 bg-gradient-to-br from-stone-950 via-stone-900 to-orange-950">
        <div className="mx-auto max-w-5xl px-6 py-20 text-center lg:px-8">
          <div className="mb-5 inline-flex rounded-full border border-orange-400/30 bg-orange-500/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-orange-300">
            Legal • Privacy • Data Protection
          </div>

          <h1 className="text-4xl font-black tracking-tight text-white sm:text-5xl lg:text-6xl">
            Privacy Policy
          </h1>

          <p className="mx-auto mt-6 max-w-3xl text-base leading-8 text-stone-300 sm:text-lg">
            This Privacy Policy explains how {COMPANY_NAME}, operating the{" "}
            {PRODUCT_NAME} platform, collects, uses, stores, protects and
            shares information when you visit our website, create an account,
            use our platform, contact us, or purchase our services.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-sm">
            <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-stone-300">
              Effective date: {EFFECTIVE_DATE}
            </span>

            <span className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-stone-300">
              India
            </span>
          </div>
        </div>
      </section>

      {/* Main */}
      <main className="mx-auto max-w-6xl px-6 py-14 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[250px_minmax(0,1fr)]">
          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
              <p className="mb-4 text-xs font-black uppercase tracking-[0.18em] text-stone-400">
                On this page
              </p>

              <nav className="space-y-1">
                {[
                  ["#introduction", "Introduction"],
                  ["#information", "Information We Collect"],
                  ["#business-data", "Business Data"],
                  ["#guest-data", "Guest & Customer Data"],
                  ["#usage", "How We Use Data"],
                  ["#payments", "Payments"],
                  ["#razorpay", "Razorpay Payments"],
                  ["#upi", "Direct UPI Payments"],
                  ["#sharing", "Data Sharing"],
                  ["#cookies", "Cookies"],
                  ["#security", "Security"],
                  ["#retention", "Data Retention"],
                  ["#rights", "Your Privacy Rights"],
                  ["#children", "Children"],
                  ["#third-party", "Third-Party Services"],
                  ["#changes", "Policy Changes"],
                  ["#contact", "Contact Us"],
                ].map(([href, label]) => (
                  <a
                    key={href}
                    href={href}
                    className="block rounded-lg px-3 py-2 text-sm text-stone-600 transition-colors hover:bg-orange-50 hover:text-orange-600"
                  >
                    {label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <article className="rounded-3xl border border-stone-200 bg-white p-7 shadow-sm sm:p-10 lg:p-12">
            {/* 01 */}
            <section id="introduction" className="scroll-mt-28">
              <SectionTitle number="01" title="Introduction" />

              <p>
                {COMPANY_NAME} ("SSQUARE LOGIC", "we", "us" or "our") operates
                and provides {PRODUCT_NAME}, a technology platform designed
                for restaurants, hotels and other hospitality businesses.
              </p>

              <p>
                {PRODUCT_NAME} may provide tools including point-of-sale,
                ordering, QR ordering, kitchen workflows, billing, inventory,
                reservations, staff management, reporting, analytics and
                related hospitality-management functionality.
              </p>

              <p>
                This Privacy Policy explains how we handle personal data and
                other information collected through our website, applications,
                software, forms, customer support channels and related
                services.
              </p>

              <InfoBox>
                By using {PRODUCT_NAME}, you acknowledge that you have read
                this Privacy Policy. Where consent is required by applicable
                law, we will obtain consent through an appropriate notice or
                consent mechanism.
              </InfoBox>
            </section>

            <Divider />

            {/* 02 */}
            <section id="information" className="scroll-mt-28">
              <SectionTitle number="02" title="Information We Collect" />

              <p>
                Depending on how you interact with {PRODUCT_NAME}, we may
                collect the following categories of information.
              </p>

              <h3>Account information</h3>

              <BulletList
                items={[
                  "Name",
                  "Email address",
                  "Mobile or telephone number",
                  "Login credentials and authentication information",
                  "Business or organization name",
                  "Account preferences and settings",
                ]}
              />

              <h3>Contact information</h3>

              <BulletList
                items={[
                  "Information submitted through contact forms",
                  "Business location or address",
                  "City, state and country",
                  "Information included in enquiries or support requests",
                ]}
              />

              <h3>Technical information</h3>

              <BulletList
                items={[
                  "IP address",
                  "Browser type and version",
                  "Device and operating-system information",
                  "Pages or screens visited",
                  "Approximate usage and session information",
                  "Cookies and similar technologies",
                ]}
              />
            </section>

            <Divider />

            {/* 03 */}
            <section id="business-data" className="scroll-mt-28">
              <SectionTitle number="03" title="Business & Operational Data" />

              <p>
                Customers using {PRODUCT_NAME} may provide business or
                operational information necessary to run their restaurant,
                hotel or hospitality business.
              </p>

              <BulletList
                items={[
                  "Menus, products and services",
                  "Table and room information",
                  "Orders and order history",
                  "Inventory and recipe information",
                  "Staff and role information",
                  "Reservations",
                  "Invoices and billing information",
                  "Business reports and analytics",
                  "Customer or guest records entered by the business",
                  "Other information required to operate the customer's account",
                ]}
              />

              <p>
                The customer business is responsible for ensuring that it has
                an appropriate legal basis and required permissions to provide
                personal information of its employees, customers, guests or
                other individuals to the platform.
              </p>
            </section>

            <Divider />

            {/* 04 */}
            <section id="guest-data" className="scroll-mt-28">
              <SectionTitle number="04" title="Guest & Customer Data" />

              <p>
                When a restaurant or hotel uses {PRODUCT_NAME} to manage guest
                or customer interactions, the business may enter or collect
                information such as:
              </p>

              <BulletList
                items={[
                  "Customer or guest name",
                  "Phone number",
                  "Email address",
                  "Order details",
                  "Reservation information",
                  "Table or room information",
                  "Billing and transaction references",
                  "Customer preferences where voluntarily provided",
                ]}
              />

              <p>
                Such information is processed to provide the functionality
                requested by the business and its customers, including
                ordering, reservations, billing, support, reporting and
                service management.
              </p>
            </section>

            <Divider />

            {/* 05 */}
            <section id="usage" className="scroll-mt-28">
              <SectionTitle number="05" title="How We Use Information" />

              <p>
                We may use information collected through {PRODUCT_NAME} for
                purposes including:
              </p>

              <BulletList
                items={[
                  "Creating and maintaining user accounts",
                  "Providing and operating the platform",
                  "Processing subscriptions and purchases",
                  "Processing and confirming payments",
                  "Providing customer support",
                  "Responding to enquiries",
                  "Sending service and transaction-related communications",
                  "Improving platform functionality and user experience",
                  "Monitoring performance and reliability",
                  "Preventing fraud, abuse and unauthorized access",
                  "Maintaining security and system integrity",
                  "Complying with legal and regulatory obligations",
                  "Maintaining appropriate business and financial records",
                ]}
              />
            </section>

            <Divider />

            {/* 06 */}
            <section id="payments" className="scroll-mt-28">
              <SectionTitle number="06" title="Payments" />

              <p>
                {PRODUCT_NAME} may offer paid subscriptions, plans, trials,
                upgrades, credits or other paid services.
              </p>

              <p>
                Payments may be accepted through third-party payment
                processors, including Razorpay, as well as through direct UPI
                payment methods made available by {COMPANY_NAME}.
              </p>

              <p>
                Payment-related information may include the amount paid,
                transaction identifier, payment status, payment date, payment
                method, customer details and other information necessary to
                identify and reconcile a transaction.
              </p>

              <InfoBox>
                We do not intend to store complete card numbers, CVV numbers,
                UPI PINs, banking passwords or other authentication secrets on
                our own application servers. Such sensitive payment
                authentication is handled by the applicable payment provider
                or banking/UPI system.
              </InfoBox>
            </section>

            <Divider />

            {/* 07 */}
            <section id="razorpay" className="scroll-mt-28">
              <SectionTitle number="07" title="Razorpay Payments" />

              <p>
                Where Razorpay is used to process a payment, customers may be
                redirected to or interact with Razorpay's payment interface.
                Razorpay may process payment and transaction information in
                accordance with its own terms and privacy practices.
              </p>

              <p>
                We may receive information from Razorpay necessary to confirm
                and reconcile a transaction, such as payment status,
                transaction identifiers, order references, amount and
                customer-related information made available as part of the
                transaction.
              </p>

              <p>
  Razorpay supports payment links and UPI payment flows for
  merchants. Payment confirmation and server-side verification
  should be performed using the applicable payment-provider
  mechanisms rather than relying solely on information supplied
  by a browser or customer.
</p>

              <p>
                For more information about how Razorpay handles personal
                information, customers should review Razorpay's applicable
                privacy notice and terms.
              </p>
            </section>

            <Divider />

            {/* 08 */}
            <section id="upi" className="scroll-mt-28">
              <SectionTitle number="08" title="Direct UPI Payments" />

              <p>
                {COMPANY_NAME} may also accept direct UPI payments where
                customers choose to pay directly to a UPI identifier, QR
                code, bank account or other payment method communicated by the
                business.
              </p>

              <p>
                For reconciliation and account activation purposes, we may ask
                the customer to provide information such as:
              </p>

              <BulletList
                items={[
                  "Name or account name",
                  "Email address or mobile number",
                  "Amount paid",
                  "UPI transaction/reference ID",
                  "Date and approximate time of payment",
                  "Payment screenshot or receipt, where voluntarily submitted",
                ]}
              />

              <p>
                Customers should never provide their UPI PIN, banking password,
                OTP, card PIN, CVV or other authentication credentials to
                {COMPANY_NAME} through a form, email, chat or support request.
              </p>

              <InfoBox>
                If a customer sends a payment screenshot or transaction
                receipt, we will use it only for purposes such as payment
                verification, reconciliation, customer support, fraud
                prevention and related legitimate business purposes.
              </InfoBox>
            </section>

            <Divider />

            {/* 09 */}
            <section id="sharing" className="scroll-mt-28">
              <SectionTitle number="09" title="When We Share Information" />

              <p>
                We do not sell personal information as a business model.
              </p>

              <p>
                Information may be shared with carefully selected service
                providers where necessary to operate {PRODUCT_NAME}.
              </p>

              <BulletList
                items={[
                  "Payment processors and payment service providers",
                  "Cloud hosting and infrastructure providers",
                  "Email, SMS and communication providers",
                  "Security and fraud-prevention providers",
                  "Analytics and monitoring providers",
                  "Professional advisers where reasonably necessary",
                  "Government, law-enforcement or regulatory authorities where legally required",
                ]}
              />

              <p>
                We may also disclose information where reasonably necessary to
                protect our rights, users, systems, property or the security
                of the platform, or in connection with a merger, acquisition,
                restructuring or transfer of business assets.
              </p>
            </section>

            <Divider />

            {/* 10 */}
            <section id="cookies" className="scroll-mt-28">
              <SectionTitle number="10" title="Cookies & Similar Technologies" />

              <p>
                Our website and platform may use cookies, local storage and
                similar technologies to provide functionality, maintain
                sessions, remember preferences, understand usage and improve
                performance.
              </p>

              <p>
                Some cookies may be provided by third-party services used on
                our website.
              </p>

              <p>
                You can control cookies through your browser settings.
                Disabling certain cookies may affect the functionality of parts
                of the website or platform.
              </p>
            </section>

            <Divider />

            {/* 11 */}
            <section id="security" className="scroll-mt-28">
              <SectionTitle number="11" title="Security" />

              <p>
                We use reasonable technical and organizational safeguards
                intended to protect information against unauthorized access,
                alteration, disclosure, misuse or destruction.
              </p>

              <BulletList
                items={[
                  "Access controls and authentication",
                  "Reasonable application and infrastructure security measures",
                  "Controlled access to business and customer information",
                  "Monitoring and logging where appropriate",
                  "Secure communication mechanisms where supported",
                  "Security reviews and improvements as the platform evolves",
                ]}
              />

              <p>
                However, no internet service, application, database or
                electronic transmission can be guaranteed to be completely
                secure. Users should maintain strong passwords and protect
                access to their accounts and devices.
              </p>
            </section>

            <Divider />

            {/* 12 */}
            <section id="retention" className="scroll-mt-28">
              <SectionTitle number="12" title="Data Retention" />

              <p>
                We retain personal and business information only for as long as
                reasonably necessary for the purposes described in this policy,
                including:
              </p>

              <BulletList
                items={[
                  "Providing and maintaining the service",
                  "Maintaining account and transaction records",
                  "Providing customer support",
                  "Resolving disputes",
                  "Preventing fraud and abuse",
                  "Meeting contractual, tax, accounting or legal obligations",
                  "Enforcing our agreements and protecting legitimate business interests",
                ]}
              />

              <p>
                Different types of information may have different retention
                periods depending on their purpose and applicable legal
                requirements.
              </p>
            </section>

            <Divider />

            {/* 13 */}
            <section id="rights" className="scroll-mt-28">
              <SectionTitle number="13" title="Your Privacy Rights" />

              <p>
                Subject to applicable law, individuals may have rights relating
                to their personal data, including rights to request access,
                correction, updating or deletion of certain information.
              </p>

              <p>
                Where processing is based on consent, applicable law may also
                provide a mechanism to withdraw that consent.
              </p>

              <p>
                Requests may be subject to reasonable verification so that we
                can protect accounts and prevent unauthorized requests.
              </p>

              <InfoBox>
                To make a privacy request, please contact {COMPANY_NAME}
                using the contact details published on our website.
              </InfoBox>
            </section>

            <Divider />

            {/* 14 */}
            <section id="children" className="scroll-mt-28">
              <SectionTitle number="14" title="Children's Privacy" />

              <p>
                {PRODUCT_NAME} is a business-oriented hospitality platform
                intended for restaurants, hotels and other hospitality
                businesses and their authorized users.
              </p>

              <p>
                We do not knowingly design our services to target children as
                users. If we become aware that personal information has been
                submitted by a child in circumstances where collection is not
                permitted, we will take reasonable steps to address the
                situation in accordance with applicable law.
              </p>
            </section>

            <Divider />

            {/* 15 */}
            <section id="third-party" className="scroll-mt-28">
              <SectionTitle number="15" title="Third-Party Services & Links" />

              <p>
                {PRODUCT_NAME} may integrate with or link to third-party
                services, websites and platforms.
              </p>

              <p>
                These third parties may have their own privacy policies and
                terms. Their processing of information is governed by their
                respective policies rather than this Privacy Policy.
              </p>

              <p>
                Third-party services may include payment providers,
                infrastructure providers, communication services, analytics
                tools, mapping services and other integrations used to operate
                or improve the platform.
              </p>
            </section>

            <Divider />

            {/* 16 */}
            <section id="changes" className="scroll-mt-28">
              <SectionTitle number="16" title="Changes to This Privacy Policy" />

              <p>
                We may update this Privacy Policy from time to time to reflect
                changes to our products, services, technology, business
                practices or applicable legal requirements.
              </p>

              <p>
                When material changes are made, we may update the effective
                date and, where appropriate, provide additional notice through
                the website, platform or other suitable communication channel.
              </p>
            </section>

            <Divider />

            {/* 17 */}
            <section id="contact" className="scroll-mt-28">
              <SectionTitle number="17" title="Contact Us" />

              <div className="rounded-2xl border border-orange-200 bg-orange-50 p-6">
                <p className="text-xl font-black text-stone-950">
                  {COMPANY_NAME}
                </p>

                <p className="mt-2 text-sm font-semibold text-orange-600">
                  Developer & Operator of {PRODUCT_NAME}
                </p>

                <p className="mt-5 text-sm leading-7 text-stone-600">
                  For privacy questions, data-related requests, account
                  concerns or questions regarding this Privacy Policy, please
                  contact us through the official contact details provided on
                  our Contact page.
                </p>

                <Link
                  to="/#contact"
                  className="mt-5 inline-flex rounded-xl bg-orange-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-orange-500/20 transition-all hover:bg-orange-600"
                >
                  Contact {COMPANY_NAME} →
                </Link>
              </div>
            </section>

            {/* Legal note */}
            <div className="mt-12 rounded-2xl border border-stone-200 bg-stone-50 p-6 text-sm leading-7 text-stone-500">
              <strong className="text-stone-800">
                Important legal notice:
              </strong>{" "}
              This Privacy Policy is intended to describe the current
              information practices of {COMPANY_NAME} and {PRODUCT_NAME}. It
              should be reviewed by the company's legal or compliance adviser
              before production publication, particularly with respect to the
              company's actual registered legal name, address, contact details,
              applicable Indian privacy requirements, payment arrangements,
              retention periods, cookies, vendors and any sector-specific
              obligations.
            </div>
          </article>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-800 bg-stone-950 text-white">
        <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-500 text-xl">
                  🔥
                </div>

                <div>
                  <p className="text-lg font-black">
                    {PRODUCT_NAME}{" "}
                    <span className="text-orange-500">OS</span>
                  </p>

                  <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
                    Hospitality Platform
                  </p>
                </div>
              </div>

              <p className="mt-5 max-w-xl text-sm leading-7 text-stone-400">
                A connected technology platform for modern restaurants,
                hotels and hospitality businesses.
              </p>

              <p className="mt-5 text-sm font-semibold text-stone-300">
                Designed & developed by{" "}
                <span className="text-orange-400">{COMPANY_NAME}</span>
              </p>
            </div>

            <div>
              <p className="mb-4 text-xs font-black uppercase tracking-[0.18em] text-stone-500">
                Product
              </p>

              <div className="space-y-3 text-sm text-stone-400">
                <Link className="block hover:text-white" to="/">
                  Platform
                </Link>
                <Link className="block hover:text-white" to="/#features">
                  Features
                </Link>
                <Link className="block hover:text-white" to="/#pricing">
                  Pricing
                </Link>
                <Link className="block hover:text-white" to="/#how-it-works">
                  How it works
                </Link>
              </div>
            </div>

            <div>
              <p className="mb-4 text-xs font-black uppercase tracking-[0.18em] text-stone-500">
                Legal
              </p>

              <div className="space-y-3 text-sm">
                <Link
                  className="block text-orange-400"
                  to="/privacy-policy"
                >
                  Privacy Policy
                </Link>

                <Link
                  className="block text-stone-400 hover:text-white"
                  to="/terms-and-conditions"
                >
                  Terms & Conditions
                </Link>

                <Link
                  className="block text-stone-400 hover:text-white"
                  to="/refund-policy"
                >
                  Refund Policy
                </Link>

                <Link
                  className="block text-stone-400 hover:text-white"
                  to="/cookie-policy"
                >
                  Cookie Policy
                </Link>
              </div>
            </div>
          </div>

          <div className="mt-10 border-t border-stone-800 pt-6 text-xs text-stone-500">
            © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
            {PRODUCT_NAME} is a product/platform operated by {COMPANY_NAME}.
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ---------- Small reusable components ---------- */

function SectionTitle({ number, title }) {
  return (
    <div className="mb-6 flex items-start gap-4">
      <span className="mt-1 flex h-8 min-w-8 items-center justify-center rounded-lg bg-orange-100 px-2 text-xs font-black text-orange-600">
        {number}
      </span>

      <h2 className="text-2xl font-black tracking-tight text-stone-950 sm:text-3xl">
        {title}
      </h2>
    </div>
  );
}

function Divider() {
  return <div className="my-12 h-px bg-stone-200" />;
}

function BulletList({ items }) {
  return (
    <ul className="mt-4 space-y-3">
      {items.map((item) => (
        <li
          key={item}
          className="flex gap-3 text-sm leading-7 text-stone-600 sm:text-base"
        >
          <span className="mt-3 h-2 w-2 shrink-0 rounded-full bg-orange-500" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function InfoBox({ children }) {
  return (
    <div className="mt-6 rounded-2xl border border-orange-200 bg-orange-50 p-5 text-sm leading-7 text-stone-700">
      <span className="font-black text-orange-600">Note: </span>
      {children}
    </div>
  );
}